import React, { useState, useRef, useEffect } from 'react';
import './ChatPanel.css';
import { useLLM, WEBLLM_MODELS, SYSTEM_PROMPT } from '../hooks/useLLM';
import { search, getChapterVerses, buildPrompt, verifyCitations } from '../utils/bibleRag';
import { bibleApi } from '../api';


function buildFocusedPrompt({ query, focusedVerse, simple }) {
  const { book, chapter, verse, verseText } = focusedVerse;
  const ref = `${book} ${chapter}:${verse}`;

  if (simple) {
    return `Verse: [${ref}] ${verseText}\n\nQuestion: ${query}\n\nAnswer using only the verse above.`;
  }

  return (
    `VERSE IN FOCUS:\n[${ref}] ${verseText}\n\n` +
    `QUESTION: ${query}\n\n` +
    `INSTRUCTIONS:\nAnswer based ONLY on the verse above. Do not use knowledge from outside the provided text.`
  );
}

const CHAT_UI = {
  en: {
    smallModelWarning: '⚠ 1B models may give poor answers. For better results, reload with Llama 3B or Phi 3.5.',
    emptyGeneral: 'Ask anything about the current passage or search across the whole Bible.',
    emptyFocused: (book, ch, v) => `Ask anything about ${book} ${ch}:${v}. The AI will answer from the loaded commentaries.`,
    placeholderReady: 'Ask about this passage…',
    placeholderFocused: (book, ch, v) => `Ask about ${book} ${ch}:${v}…`,
    placeholderNotReady: 'Configure AI above to start…',
  },
  zh: {
    smallModelWarning: '⚠ 1B 模型答案品質較差。建議重新載入 Llama 3B 或 Phi 3.5 以獲得更好的結果。',
    emptyGeneral: '詢問當前段落的任何問題，或搜索整本聖經。',
    emptyFocused: (book, ch, v) => `詢問關於 ${book} ${ch}:${v} 的問題。AI 將從已載入的注釋中回答。`,
    placeholderReady: '詢問關於本段落的問題…',
    placeholderFocused: (book, ch, v) => `詢問關於 ${book} ${ch}:${v} 的問題…`,
    placeholderNotReady: '請先設定 AI…',
  },
  'zh-s': {
    smallModelWarning: '⚠ 1B 模型答案质量较差。建议重新加载 Llama 3B 或 Phi 3.5 以获得更好的结果。',
    emptyGeneral: '询问当前段落的任何问题，或搜索整本圣经。',
    emptyFocused: (book, ch, v) => `询问关于 ${book} ${ch}:${v} 的问题。AI 将从已加载的注释中回答。`,
    placeholderReady: '询问关于本段落的问题…',
    placeholderFocused: (book, ch, v) => `询问关于 ${book} ${ch}:${v} 的问题…`,
    placeholderNotReady: '请先设置 AI…',
  },
  fr: {
    smallModelWarning: '⚠ Les modèles 1B peuvent donner de mauvaises réponses. Pour de meilleurs résultats, rechargez avec Llama 3B ou Phi 3.5.',
    emptyGeneral: 'Posez une question sur le passage actuel ou cherchez dans toute la Bible.',
    emptyFocused: (book, ch, v) => `Posez une question sur ${book} ${ch}:${v}. L'IA répondra à partir des commentaires chargés.`,
    placeholderReady: 'Posez une question sur ce passage…',
    placeholderFocused: (book, ch, v) => `Posez une question sur ${book} ${ch}:${v}…`,
    placeholderNotReady: "Configurez l'IA ci-dessus pour commencer…",
  },
};

// ── Session helpers ───────────────────────────────────────────────────────────

function makeSession(verseRef = null) {
  return {
    id: `sess_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    startedAt: Date.now(),
    verseRef: verseRef || null,
    preview: '',
    messages: [],
  };
}

function loadSessions() {
  try {
    return JSON.parse(localStorage.getItem('plutobible-chat-sessions') || '[]');
  } catch { return []; }
}

function persistSessions(sessions) {
  try {
    localStorage.setItem('plutobible-chat-sessions', JSON.stringify(sessions.slice(0, 50)));
  } catch {}
}

function initChatState() {
  let saved = loadSessions();
  // Migrate from old single-history key
  if (saved.length === 0) {
    try {
      const old = localStorage.getItem('plutobible-chat-history');
      if (old) {
        const msgs = JSON.parse(old);
        if (msgs?.length > 0) {
          const migrated = makeSession(null);
          migrated.preview = msgs.find(m => m.role === 'user')?.display?.slice(0, 60) || '';
          migrated.messages = msgs;
          saved = [migrated];
          localStorage.removeItem('plutobible-chat-history');
        }
      }
    } catch {}
  }
  // Always ensure a fresh empty session is at the front for the active chat
  if (saved.length === 0 || saved[0].messages.length > 0) {
    const fresh = makeSession(null);
    saved = [fresh, ...saved].slice(0, 50);
  }
  persistSessions(saved);
  return { sessions: saved, activeId: saved[0].id };
}

function formatSessionDate(ts) {
  const d = new Date(ts);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === now.toDateString()) return `Today · ${time}`;
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday · ${time}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ` · ${time}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ChatPanel({ bibleData, ragIndex, bookIdx, chapterIdx, focusedVerse, onClose, language, user, isSubscribed }) {
  const ui = CHAT_UI[language] || CHAT_UI.en;
  const llm = useLLM();

  // ── Session state ────────────────────────────────────────────────────────────
  const [initData] = useState(initChatState);
  const [sessions, setSessions] = useState(initData.sessions);
  const [activeId, setActiveId] = useState(initData.activeId);
  const [messages, setMessages] = useState(
    () => initData.sessions.find(s => s.id === initData.activeId)?.messages || []
  );
  const [showHistory, setShowHistory] = useState(false);
  const [viewingId, setViewingId]     = useState(null); // session ID being viewed read-only

  const [input, setInput]               = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiUsage, setAiUsage]           = useState(
    // Pre-populate from userProfile if available (loaded on login)
    () => {
      if (isSubscribed) {
        const stored = localStorage.getItem('plutobible-ai-usage');
        return stored ? JSON.parse(stored) : null;
      }
      return null;
    }
  );
  const [webllmChoice, setWebllmChoice] = useState(
    () => localStorage.getItem('plutobible-webllm-last-model') || WEBLLM_MODELS[0].id
  );
  // Keep webllmChoice in sync when the loaded model changes (e.g. on auto-load)
  useEffect(() => {
    if (llm.webllmModel) setWebllmChoice(llm.webllmModel);
  }, [llm.webllmModel]);

  // Keep provider in sync with subscription state
  useEffect(() => {
    if (user && isSubscribed) {
      llm.setActiveProvider('deepseek');
    } else if (llm.activeProvider === 'deepseek') {
      llm.setActiveProvider('ollama');
    }
  }, [user, isSubscribed]); // eslint-disable-line react-hooks/exhaustive-deps
  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);

  // ── Sync messages → active session ───────────────────────────────────────────
  useEffect(() => {
    if (!activeId) return;
    setSessions(prev => {
      const firstUser = messages.find(m => m.role === 'user');
      const preview = firstUser?.display?.slice(0, 60) || '';
      const updated = prev.map(s =>
        s.id === activeId ? { ...s, messages, preview } : s
      );
      persistSessions(updated);
      return updated;
    });
  }, [messages, activeId]);

  useEffect(() => {
    if (!showHistory) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, showHistory]);

  // ── New Chat ──────────────────────────────────────────────────────────────────
  function handleNewChat() {
    if (messages.length === 0) return;
    const fresh = makeSession(null);
    setSessions(prev => {
      const updated = [fresh, ...prev].slice(0, 50);
      persistSessions(updated);
      return updated;
    });
    setActiveId(fresh.id);
    setMessages([]);
    setShowHistory(false);
    setViewingId(null);
  }

  // ── Send ─────────────────────────────────────────────────────────────────────
  async function handleSend() {
    const query = input.trim();
    if (!query || isGenerating || !llm.isReady) return;

    setInput('');
    setIsGenerating(true);

    const userMsg = { role: 'user', display: query, timestamp: Date.now() };
    const aiMsg   = { role: 'assistant', content: '', warnings: [] };

    try {
      // Capture verseRef on first message of a session
      if (messages.length === 0 && focusedVerse) {
        const ref = `${focusedVerse.book} ${focusedVerse.chapter}:${focusedVerse.verse}`;
        setSessions(prev => {
          const updated = prev.map(s => s.id === activeId ? { ...s, verseRef: ref } : s);
          persistSessions(updated);
          return updated;
        });
      }

      // In-browser (WebLLM) models always use simple prompt — they're slower to process large contexts
      const isSmallModel = llm.activeProvider === 'webllm' || /1B|2B|3B/i.test(llm.webllmModel);
      let augmentedQuery;
      if (focusedVerse) {
        augmentedQuery = buildFocusedPrompt({ query, focusedVerse, simple: isSmallModel });
      } else {
        const currentChapter = getChapterVerses(bibleData, bookIdx, chapterIdx);
        let ragResults;
        if (llm.activeProvider === 'deepseek') {
          // Semantic search via backend — more relevant results for natural language queries
          ragResults = await bibleApi.semanticSearch(query, 10);
          // Fall back to BM25 if the index isn't ready yet
          if (ragResults.length === 0) ragResults = search(query, ragIndex, 6);
        } else {
          // BM25 for local providers (Ollama / WebLLM) — no network round-trip needed
          ragResults = search(query, ragIndex, 6);
        }
        augmentedQuery = buildPrompt({ query, ragResults, currentChapterVerses: currentChapter, simple: isSmallModel });
      }

      // Add both messages now — user sees thinking dots immediately
      setMessages(prev => [...prev, userMsg, aiMsg]);

      const LANG_INSTRUCTIONS = {
        'zh':   'Always respond in Traditional Chinese (繁體中文). The Scripture passages are in English — translate any verse you quote into Traditional Chinese.',
        'zh-s': 'Always respond in Simplified Chinese (简体中文). The Scripture passages are in English — translate any verse you quote into Simplified Chinese.',
        'fr':   'Always respond in French (français). The Scripture passages are in English — translate any verse you quote into French.',
      };
      const langInstruction = LANG_INSTRUCTIONS[language] || 'Always respond in the same language as the user\'s question.';
      const systemPrompt = `${SYSTEM_PROMPT} ${langInstruction}`;

      let fullText = '';
      await llm.generate({
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user',   content: augmentedQuery },
        ],
        onUsage: (usage) => {
          setAiUsage(usage);
          try { localStorage.setItem('plutobible-ai-usage', JSON.stringify(usage)); } catch {}
        },
        onToken: (token) => {
          fullText += token;
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...aiMsg, content: fullText };
            return updated;
          });
        },
        onDone: () => {
          const finalText = fullText || '(No response — try a different model or reload the page.)';
          const warnings = verifyCitations(finalText, bibleData);
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...aiMsg, content: finalText, warnings };
            return updated;
          });
          setIsGenerating(false);
          inputRef.current?.focus();
        },
        onError: (e) => {
          console.error('LLM error:', e);
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...aiMsg, content: `Error: ${e?.message || 'Generation failed. Please try again.'}` };
            return updated;
          });
          setIsGenerating(false);
        },
      });
    } catch (e) {
      console.error('handleSend error:', e);
      setMessages(prev => {
        // Only patch the AI bubble if it was already added, otherwise add fresh
        const hasAiMsg = prev.length > 0 && prev[prev.length - 1].role === 'assistant';
        if (hasAiMsg) {
          const updated = [...prev];
          updated[updated.length - 1] = { ...aiMsg, content: `Error: ${e?.message || 'Something went wrong.'}` };
          return updated;
        }
        return [...prev, userMsg, { ...aiMsg, content: `Error: ${e?.message || 'Something went wrong.'}` }];
      });
      setIsGenerating(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  // ── Provider tabs ─────────────────────────────────────────────────────────────
  function renderProviderTabs() {
    if (llm.detecting) {
      return <span className="Chat-detecting">Detecting AI…</span>;
    }
    return (
      <div className="Chat-provider-tabs">
        {user && isSubscribed && (
          <button
            className={`Chat-tab ${llm.activeProvider === 'deepseek' ? 'active' : ''}`}
            onClick={() => llm.setActiveProvider('deepseek')}
            title="PlutoBible AI"
          >
            PlutoBible AI
            <span className="Chat-tab-dot available" />
          </button>
        )}
        <button
          className={`Chat-tab ${llm.activeProvider === 'ollama' ? 'active' : ''} ${!llm.ollamaAvailable ? 'unavailable' : ''}`}
          onClick={() => llm.setActiveProvider('ollama')}
          title={llm.ollamaAvailable ? 'Use Ollama (local server)' : 'Ollama not detected — install and run ollama serve'}
        >
          Ollama
          {llm.ollamaAvailable
            ? <span className="Chat-tab-dot available" />
            : <span className="Chat-tab-dot unavailable" />}
        </button>
        <button
          className={`Chat-tab ${llm.activeProvider === 'webllm' ? 'active' : ''} ${!llm.webgpuAvailable ? 'unavailable' : ''}`}
          onClick={() => llm.setActiveProvider('webllm')}
          title={llm.webgpuAvailable ? 'Use in-browser AI (WebGPU)' : 'WebGPU not supported — use Chrome or Edge'}
        >
          In-Browser
          {llm.webgpuAvailable
            ? <span className="Chat-tab-dot available" />
            : <span className="Chat-tab-dot unavailable" />}
        </button>
      </div>
    );
  }

  // ── Provider config panel ─────────────────────────────────────────────────────
  function renderProviderConfig() {
    if (llm.activeProvider === 'deepseek') {
      const remaining = aiUsage ? aiUsage.limit - aiUsage.used : null;
      const limitReached = remaining !== null && remaining <= 0;
      return (
        <div className="Chat-provider-config">
          <span className={`Chat-config-label ${limitReached ? 'Chat-config-limit' : 'Chat-config-ready'}`}>
            {limitReached
              ? '✗ Monthly limit reached — resets 1st of next month'
              : '✓ Ready'}
          </span>
          {!limitReached && (
            <span className="Chat-usage-badge" title={aiUsage ? `${aiUsage.used} of ${aiUsage.limit} messages used this month` : '300 messages per month'}>
              {remaining !== null ? `${remaining} / ${aiUsage.limit} left` : '300 msgs/month'}
            </span>
          )}
        </div>
      );
    }

    if (llm.activeProvider === 'ollama') {
      if (!llm.ollamaAvailable) {
        return (
          <div className="Chat-provider-hint">
            Ollama is not running.{' '}
            <a href="https://ollama.com" target="_blank" rel="noreferrer">Install Ollama</a>
            {' '}then run: <code>ollama run llama3.2</code>
          </div>
        );
      }
      return (
        <div className="Chat-provider-config">
          <span className="Chat-config-label">Model:</span>
          <select
            className="Chat-model-select"
            value={llm.ollamaModel}
            onChange={e => llm.setOllamaModel(e.target.value)}
          >
            {llm.ollamaModels.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      );
    }

    if (llm.activeProvider === 'webllm') {
      if (llm.webllmStatus === 'idle' || llm.webllmStatus === 'error') {
        return (
          <div className="Chat-provider-config">
            <select
              className="Chat-model-select"
              value={webllmChoice}
              onChange={e => setWebllmChoice(e.target.value)}
            >
              {WEBLLM_MODELS.map(m => (
                <option key={m.id} value={m.id}>{m.label} [{m.vram}]</option>
              ))}
            </select>
            <button className="Chat-load-btn" onClick={() => llm.loadWebLLMModel(webllmChoice)}>
              {llm.webllmStatus === 'error' ? 'Retry' : 'Load Model'}
            </button>
            {llm.webllmStatus === 'error' && (
              <span className="Chat-error-text">Load failed — check browser console.</span>
            )}
            {!llm.webgpuAvailable && (
              <span className="Chat-error-text">WebGPU not detected — requires Chrome or Edge.</span>
            )}
          </div>
        );
      }
      if (llm.webllmStatus === 'loading') {
        return (
          <div className="Chat-load-progress">
            <div className="Chat-progress-bar">
              <div className="Chat-progress-fill" style={{ width: `${llm.loadProgress}%` }} />
            </div>
            <span className="Chat-progress-text">{llm.loadText || `${llm.loadProgress}%`}</span>
          </div>
        );
      }
      if (llm.webllmStatus === 'ready') {
        const isSmall = /1B|2B|3B/i.test(llm.webllmModel);
        const isSwitching = webllmChoice !== llm.webllmModel;
        return (
          <div className="Chat-provider-config" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
              <span className="Chat-config-label">Model:</span>
              <select
                className="Chat-model-select"
                value={webllmChoice}
                onChange={e => setWebllmChoice(e.target.value)}
                style={{ flex: 1 }}
              >
                {WEBLLM_MODELS.map(m => (
                  <option key={m.id} value={m.id}>{m.label} [{m.vram}]</option>
                ))}
              </select>
              {isSwitching && (
                <button className="Chat-load-btn" onClick={() => llm.loadWebLLMModel(webllmChoice)}>
                  Switch
                </button>
              )}
            </div>
            {isSmall && (
              <div className="Chat-model-warning">
                {ui.smallModelWarning}
              </div>
            )}
          </div>
        );
      }
    }
    return null;
  }

  // ── History panel ─────────────────────────────────────────────────────────────
  function renderHistoryMessages(sess) {
    return sess.messages.map((msg, i) => (
      <div key={i} className={`Chat-msg ${msg.role}`}>
        {msg.role === 'user' ? (
          <div className="Chat-msg-user-wrap">
            <span className="Chat-bubble user">{msg.display}</span>
            {msg.timestamp && (
              <span className="Chat-msg-time">
                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        ) : (
          <div className="Chat-bubble assistant">
            <span className="Chat-text">{msg.content}</span>
            {msg.warnings?.length > 0 && (
              <div className="Chat-warnings">
                {msg.warnings.map((w, wi) => <div key={wi} className="Chat-warning">⚠ {w}</div>)}
              </div>
            )}
          </div>
        )}
      </div>
    ));
  }

  // ── Export ───────────────────────────────────────────────────────────────────
  function handleExportChats() {
    const nonempty = sessions.filter(s => s.messages.length > 0);
    const exportData = nonempty.map(sess => ({
      date: new Date(sess.startedAt).toISOString(),
      verseRef: sess.verseRef || null,
      messages: sess.messages.map(m =>
        m.role === 'user'
          ? { role: 'user', text: m.display, time: m.timestamp ? new Date(m.timestamp).toISOString() : null }
          : { role: 'assistant', text: m.content }
      ),
    }));
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plutobible-chats-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function renderHistory() {
    // Viewing a specific session
    if (viewingId) {
      const sess = sessions.find(s => s.id === viewingId);
      if (!sess) return null;
      return (
        <div className="Chat-history-view">
          <div className="Chat-history-nav">
            <button className="Chat-history-back" onClick={() => setViewingId(null)}>
              <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="10,3 5,8 10,13"/>
              </svg>
              History
            </button>
            <span className="Chat-history-session-date">{formatSessionDate(sess.startedAt)}</span>
          </div>
          {sess.verseRef && (
            <div className="Chat-focus-banner">
              <span className="Chat-focus-ref">📖 {sess.verseRef}</span>
            </div>
          )}
          <div className="Chat-messages Chat-history-messages">
            {renderHistoryMessages(sess)}
            <div style={{ height: '0.5rem' }} />
          </div>
        </div>
      );
    }

    // Session list
    const nonempty = sessions.filter(s => s.messages.length > 0);
    return (
      <div className="Chat-history-panel">
        <div className="Chat-history-nav">
          <button className="Chat-history-back" onClick={() => setShowHistory(false)}>
            <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="10,3 5,8 10,13"/>
            </svg>
            Chat
          </button>
          <span className="Chat-history-title">History</span>
          {nonempty.length > 0 && (
            <button
              className="Chat-export-btn"
              onClick={handleExportChats}
              title="Export all chats as JSON"
            >
              <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 2v8M5 7l3 3 3-3"/>
                <path d="M3 12h10"/>
              </svg>
              Export
            </button>
          )}
        </div>
        {nonempty.length === 0 ? (
          <div className="Chat-history-empty">No past conversations yet.</div>
        ) : (
          <div className="Chat-history-list">
            {nonempty.map(sess => (
              <button
                key={sess.id}
                className={`Chat-history-item ${sess.id === activeId ? 'Chat-history-item--active' : ''}`}
                onClick={() => {
                  if (sess.id === activeId) {
                    setShowHistory(false);
                  } else {
                    setViewingId(sess.id);
                  }
                }}
              >
                <div className="Chat-history-item-top">
                  {sess.verseRef
                    ? <span className="Chat-history-ref">{sess.verseRef}</span>
                    : <span className="Chat-history-ref Chat-history-ref--general">General</span>
                  }
                  <span className="Chat-history-date">{formatSessionDate(sess.startedAt)}</span>
                </div>
                <div className="Chat-history-preview">
                  {sess.preview ? sess.preview + (sess.preview.length >= 60 ? '…' : '') : '—'}
                  {sess.id === activeId && <span className="Chat-history-current"> · current</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="ChatPanel">
      <div className="Chat-header">
        <span className="Chat-title">Bible AI</span>
        <button
          className={`Chat-history-btn ${showHistory ? 'active' : ''}`}
          onClick={() => { setShowHistory(h => !h); setViewingId(null); }}
          title="Chat history"
          aria-label="Chat history"
        >
          <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="10" cy="10" r="8"/>
            <polyline points="10,5.5 10,10 13,12.5"/>
          </svg>
        </button>
        {!showHistory && messages.length > 0 && !isGenerating && (
          <button className="Chat-clear-btn" onClick={handleNewChat} title="Start a new chat">
            New Chat
          </button>
        )}
        <button className="Chat-close-btn" onClick={onClose} aria-label="Close chat">✕</button>
      </div>

      {showHistory ? renderHistory() : (
        <>
          <div className="Chat-controls">
            {renderProviderTabs()}
            {renderProviderConfig()}
          </div>

          {focusedVerse && (
            <div className="Chat-focus-banner">
              <span className="Chat-focus-ref">📖 {focusedVerse.book} {focusedVerse.chapter}:{focusedVerse.verse}</span>
              {Object.keys(focusedVerse.commentaries).length > 0 ? (
                <span className="Chat-focus-meta">
                  · {Object.keys(focusedVerse.commentaries).length} of 4 commentaries loaded
                </span>
              ) : (
                <span className="Chat-focus-meta">· verse only (no commentary found)</span>
              )}
            </div>
          )}

          <div className="Chat-messages">
            {!llm.isReady && messages.length === 0 && (
              <div className="Chat-not-ready">
                {llm.detecting ? (
                  <span>Detecting available AI…</span>
                ) : llm.activeProvider === 'ollama' && !llm.ollamaAvailable ? (
                  <>
                    <div className="Chat-not-ready-icon">🔌</div>
                    <div className="Chat-not-ready-title">Ollama is not running</div>
                    <div className="Chat-not-ready-body">
                      Start Ollama on your machine, then refresh this page.<br />
                      Or switch to <strong>In-Browser</strong> above.
                    </div>
                  </>
                ) : llm.activeProvider === 'webllm' && llm.webllmStatus === 'idle' ? (
                  <>
                    <div className="Chat-not-ready-icon">👆</div>
                    <div className="Chat-not-ready-title">No model loaded</div>
                    <div className="Chat-not-ready-body">
                      {localStorage.getItem('plutobible-webllm-last-model')
                        ? <>Previously used model not found in cache. Choose a model above and click <strong>Load Model</strong>.</>
                        : <>Choose a model from the dropdown above and click <strong>Load Model</strong> to download and start it.</>}
                    </div>
                  </>
                ) : llm.activeProvider === 'webllm' && llm.webllmStatus === 'error' ? (
                  <>
                    <div className="Chat-not-ready-icon">⚠</div>
                    <div className="Chat-not-ready-title">Model failed to load</div>
                    <div className="Chat-not-ready-body">
                      Click <strong>Retry</strong> above, or pick a smaller model.
                    </div>
                  </>
                ) : llm.activeProvider === 'none' ? (
                  <>
                    <div className="Chat-not-ready-icon">🚫</div>
                    <div className="Chat-not-ready-title">No AI available</div>
                    <div className="Chat-not-ready-body">
                      Install <a href="https://ollama.com" target="_blank" rel="noreferrer">Ollama</a> and run a model,
                      or open this page in Chrome / Edge to use in-browser AI.
                    </div>
                  </>
                ) : null}
              </div>
            )}
            {llm.isReady && messages.length === 0 && (
              <div className="Chat-empty">
                {focusedVerse
                  ? ui.emptyFocused(focusedVerse.book, focusedVerse.chapter, focusedVerse.verse)
                  : ui.emptyGeneral}
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`Chat-msg ${msg.role}`}>
                {msg.role === 'user' ? (
                  <div className="Chat-msg-user-wrap">
                    <span className="Chat-bubble user">{msg.display}</span>
                    {msg.timestamp && (
                      <span className="Chat-msg-time">
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="Chat-bubble assistant">
                    {isGenerating && i === messages.length - 1 && !msg.content ? (
                      <span className="Chat-thinking">
                        <span /><span /><span />
                      </span>
                    ) : (
                      <span className="Chat-text">{msg.content}</span>
                    )}
                    {msg.content && !isGenerating && msg.warnings?.length > 0 && (
                      <div className="Chat-warnings">
                        {msg.warnings.map((w, wi) => (
                          <div key={wi} className="Chat-warning">⚠ {w}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="Chat-input-row">
            <textarea
              ref={inputRef}
              className="Chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={llm.isReady
                ? (focusedVerse ? ui.placeholderFocused(focusedVerse.book, focusedVerse.chapter, focusedVerse.verse) : ui.placeholderReady)
                : ui.placeholderNotReady}
              disabled={!llm.isReady || isGenerating}
              rows={2}
            />
            {isGenerating ? (
              <button className="Chat-send-btn stop" onClick={() => { llm.cancel(); setIsGenerating(false); }}>Stop</button>
            ) : (
              <button className="Chat-send-btn" onClick={handleSend} disabled={!llm.isReady || !input.trim()}>
                Send
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
